import { useState, useCallback, useEffect, useRef } from "react";
import apiService from "../services/apiService";
import { DEFAULT_UNIT_PREFERENCES } from "../context/unit-preferences-context";

const STORAGE_KEY = "unitPreferences";

/**
 * Recursively deep-merge source into target.
 * Ensures new default keys added in future releases are always present
 * even if the stored/API value predates them.
 */
const deepMerge = (target, source) => {
  const output = { ...target };
  if (!source || typeof source !== "object") return output;
  Object.keys(source).forEach((key) => {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      output[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  });
  return output;
};

/**
 * Custom hook to manage user unit preferences.
 * - Loads from localStorage immediately for a fast first render.
 * - Fetches from the API on login and merges with defaults.
 * - Exposes updateUnitPreferences (local) and saveUnitPreferences (API + local).
 *
 * @param {string} token  - JWT token from auth context
 * @param {string} UID    - User ID from auth context
 * @returns {{ unitPreferences, updateUnitPreferences, saveUnitPreferences, isSaving, loadError }}
 */
export const useUnitPreferences = (token, UID) => {
  const [unitPreferences, setUnitPreferences] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return deepMerge(DEFAULT_UNIT_PREFERENCES, JSON.parse(stored));
      }
    } catch (err) {
      console.error("Error reading unit preferences from localStorage:", err);
    }
    return DEFAULT_UNIT_PREFERENCES;
  });

  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const preferencesFetchedRef = useRef(false);

  // Fetch from API on login
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!token || !UID || preferencesFetchedRef.current) return;

      try {
        const data = await apiService.get(`/v1/user/${UID}/preferences`, token);
        const serverPrefs = data.unitPreferences || data.preferences?.unitPreferences || data;

        if (serverPrefs && typeof serverPrefs === "object") {
          const merged = deepMerge(DEFAULT_UNIT_PREFERENCES, serverPrefs);
          setUnitPreferences(merged);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        }

        preferencesFetchedRef.current = true;
      } catch (err) {
        // 404 means no preferences saved yet — silently use defaults
        if (err.status !== 404) {
          console.error("Error fetching unit preferences:", err);
          setLoadError(err.message || "Failed to load unit preferences");
        }
        preferencesFetchedRef.current = true;
      }
    };

    if (token && UID) {
      fetchPreferences();
    } else {
      // Reset on logout so next login re-fetches
      preferencesFetchedRef.current = false;
    }
  }, [token, UID]);

  /**
   * Update preferences locally (in state + localStorage).
   * Does NOT call the API — call saveUnitPreferences to persist to the backend.
   *
   * @param {object} updatedPreferences - Full or partial preferences object
   */
  const updateUnitPreferences = useCallback((updatedPreferences) => {
    setUnitPreferences((prev) => {
      const merged = deepMerge(prev, updatedPreferences);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  }, []);

  /**
   * Persist current preferences to the backend.
   * Also updates localStorage.
   *
   * @param {object} [overridePreferences] - If provided, saves this instead of current state.
   * @returns {Promise<void>}
   */
  const saveUnitPreferences = useCallback(
    async (overridePreferences) => {
      if (!token || !UID) return;

      const toSave = overridePreferences ?? unitPreferences;

      try {
        setIsSaving(true);
        await apiService.put(
          `/v1/user/${UID}/preferences`,
          { unitPreferences: toSave },
          token
        );
        setUnitPreferences(toSave);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch (err) {
        console.error("Error saving unit preferences:", err);
        throw err; // Re-throw so the caller (e.g. UserSettingsPage) can surface the error
      } finally {
        setIsSaving(false);
      }
    },
    [token, UID, unitPreferences]
  );

  return { unitPreferences, updateUnitPreferences, saveUnitPreferences, isSaving, loadError };
};
